/**
 * @module linkage/qbFields/qbLinkage/handlers/seriesSorts
 * @description 图例设置 —「按指标排序」(`series_sorts`) 的 QB 联动
 *
 * ## 业务背景
 *
 * 图例系列排序（LegendSetting / SeriesSortBy）有三种模式，由 `sort_series` 区分：
 *
 * | sort_series | 上传字段 | 含义 |
 * |-------------|----------|------|
 * | default | 三者皆空或默认 | 跟随计算默认顺序 |
 * | metric | `series_sorts` | 用户指定「按哪个指标、升/降序」 |
 * | manual | `series_order` | 用户拖拽图例系列顺序 |
 *
 * 本模块**只**维护 `series_sorts`（`sort_series === metric` 时才有意义）。
 *
 * ## 为何不动 `series_order`
 *
 * `series_order` 存的是**计算接口返回的系列展示名**（见 `useSeriesSortBy` 从 chartData 取 seed），
 * 不是 QB 字段引用。QB 删/改字段后，用户点「计算」会拿到新系列名，面板内 `useEffect` 会用新 seed
 * 覆盖 draft 中的 `series_order`。因此不在 QB 管道里 prune/migrate `series_order`。
 *
 * ## QB 删指标
 *
 * 与 X 轴 `sortByHandler` 一致：只要当前为「按指标排序」或存在 `series_sorts`，
 * 删任一指标后**整组恢复默认排序**（`sort_series=default` 且清空 series_sorts/order），
 * 避免 `CustomMetricSort` 在空数组时自动回填剩余指标。
 *
 * @see settingGroups/LegendSetting/useSeriesSortBy.ts
 * @see settingGroups/LegendSetting/seriesSortByUtils.ts
 * @see docs/qb-to-panel-sync-design.md 矩阵 #9
 */

import type { IFieldSnapshot, IQBRenameEvent } from '../types';
import type { IQBLinkageHandler } from '../types';
import { SortByEnum } from '../../../../settingGroups/common/SortByPanel/SortType';
import { buildDefaultSeriesSortPayload } from '../../../../settingGroups/LegendSetting/seriesSortByUtils';
import {
  hasAnyConfigInLayers,
  hasNonEmptyArrayInLayers,
  metricLikeRemovedFields,
  patchBoth,
} from './shared';

const LEGEND_SORT_KEYS = ['sort_series', 'series_sorts', 'series_order'] as const;

/** 删指标后写入 base/draft 的默认图例排序（与面板「默认排序」选项一致） */
export function buildLegendSortResetPatch(): Record<string, unknown> {
  return buildDefaultSeriesSortPayload();
}

/**
 * 当前是否处于「按指标排序」或已有 series_sorts 配置。
 * 仅在此情况下 QB 删指标需要整组回默认（手动排序 series_order 不在此列）。
 */
export function shouldResetLegendSortOnMetricRemoved(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): boolean {
  const sortSeries = String(
    draft.sort_series ?? base.sort_series ?? SortByEnum.Default,
  );
  if (sortSeries === SortByEnum.Metric) {
    return true;
  }
  return hasNonEmptyArrayInLayers(base, draft, 'series_sorts');
}

/** 图例「按指标排序」单行 — 与 CustomMetricSort / buildMetricSeriesSortPayload 上传结构一致 */
export interface ISeriesSortRow {
  key: string;
  value: 'asc' | 'desc';
  table_code?: string;
}

type TSortMetricRow = {
  column_name?: string;
  metric?: string;
  key?: string;
  table_code?: string;
  tableCode?: string;
  value?: string;
};

function resolveMetricKey(row: TSortMetricRow): string {
  return String(row.column_name ?? row.metric ?? row.key ?? '');
}

function resolveTableCode(row: TSortMetricRow): string {
  return String(row.table_code ?? row.tableCode ?? 't0');
}

/**
 * QB 删指标 / 删明细 raw 列 — 从 `series_sorts` 移除已不存在的指标项。
 *
 * 匹配规则与 column_percentage 相同：`key` === column_name 且 table_code 一致。
 * 仅用于单测或局部裁剪；QB 删指标请走 `buildLegendSortResetPatch` 整组回默认。
 */
export function pruneSeriesSorts(
  list: ISeriesSortRow[] | undefined,
  removed: Array<{ column_name: string; table_code: string }>,
): ISeriesSortRow[] | undefined {
  if (!list?.length) {
    return undefined;
  }
  const filtered = list.filter(
    row =>
      !removed.some(
        r =>
          row.key === r.column_name &&
          (row.table_code || 't0') === (r.table_code || 't0'),
      ),
  );
  return filtered.length !== list.length ? filtered : undefined;
}

/**
 * QB 字段身份改名（column_name 变更，非仅展示名）— 同步更新 `series_sorts[].key`。
 *
 * 仅处理 `displayOnly === false` 的 rename，与 statistics / description migrate 一致。
 * 展示名改名（displayOnly）不写入 base，图例 UI 从 QB 读 label，故 no-op。
 */
export function migrateSeriesSorts(
  list: ISeriesSortRow[] | undefined,
  renames: IQBRenameEvent[],
): ISeriesSortRow[] | undefined {
  if (!list?.length || !renames.length) {
    return undefined;
  }
  const identityRenames = renames.filter(r => !r.displayOnly);
  if (!identityRenames.length) {
    return undefined;
  }
  let changed = false;
  const next = list.map(row => {
    const tc = row.table_code || 't0';
    const hit = identityRenames.find(
      r =>
        r.prevColumnName === row.key &&
        (r.field.table_code || 't0') === tc,
    );
    if (!hit) {
      return row;
    }
    changed = true;
    return { ...row, key: hit.nextColumnName };
  });
  return changed ? next : undefined;
}

/**
 * 自定义字段改名入口（`customColumnConfigRename`）与 QB 管道共用的行级 migrate。
 *
 * 同时服务 `chartSorts` / `customMetricSort` / `series_sorts`：
 * - 图例与 X 轴按指标排序行结构不同，但都可能用 `key` 引用 QB 指标名
 * - 旧实现只认 column_name/metric，导致 series_sorts 在自定义字段改名时漏迁
 */
export function migrateMetricSortRows(
  list: unknown,
  oldName: string,
  newName: string,
  tableCode: string,
): unknown {
  if (!Array.isArray(list)) {
    return undefined;
  }
  let changed = false;
  const next = list.map(row => {
    if (!row || typeof row !== 'object') {
      return row;
    }
    const r = row as TSortMetricRow;
    const name = resolveMetricKey(r);
    const tc = resolveTableCode(r);
    if (name !== oldName || tc !== (tableCode || 't0')) {
      return row;
    }
    changed = true;
    return {
      ...r,
      ...(r.column_name !== undefined ? { column_name: newName } : {}),
      ...(r.metric !== undefined ? { metric: newName } : {}),
      key: r.key !== undefined ? newName : r.key,
    };
  });
  return changed ? next : undefined;
}

/**
 * Phase prune：QB 移除指标时清理 base + draft 中的 series_sorts。
 *
 * 订阅 rawColumnRemoved：表格明细模式下 raw 列删除与聚合删指语义相同（见 shared.metricLikeRemovedFields）。
 */
export const seriesSortsHandler: IQBLinkageHandler = {
  id: 'seriesSorts',
  phase: 'prune',
  keys: [...LEGEND_SORT_KEYS],
  subscribe: ['metricRemoved', 'rawColumnRemoved'],
  apply(changeSet, ctx) {
    const removed = metricLikeRemovedFields(changeSet);
    if (!removed.length) {
      return;
    }
    if (
      !hasAnyConfigInLayers(ctx.base, ctx.draft, [...LEGEND_SORT_KEYS])
      || !shouldResetLegendSortOnMetricRemoved(ctx.base, ctx.draft)
    ) {
      return;
    }
    const resetPatch = buildLegendSortResetPatch();
    return patchBoth({ ...resetPatch }, { ...resetPatch });
  },
};

/** Phase migrate：QB 字段身份改名时迁移 series_sorts（与 statisticsMigrateHandler 同阶段） */
export const seriesSortsMigrateHandler: IQBLinkageHandler = {
  id: 'seriesSorts-migrate',
  phase: 'migrate',
  keys: ['series_sorts'],
  subscribe: ['fieldIdentityRenamed'],
  apply(changeSet, ctx) {
    if (!hasNonEmptyArrayInLayers(ctx.base, ctx.draft, 'series_sorts')) {
      return;
    }
    const baseNext = migrateSeriesSorts(
      ctx.base.series_sorts as ISeriesSortRow[] | undefined,
      changeSet.renamed,
    );
    const draftNext = migrateSeriesSorts(
      ctx.draft.series_sorts as ISeriesSortRow[] | undefined,
      changeSet.renamed,
    );
    return patchBoth(
      baseNext !== undefined ? { series_sorts: baseNext } : undefined,
      draftNext !== undefined ? { series_sorts: draftNext } : undefined,
    );
  },
};
